import config from 'config'
import { getThumbnailPath, productThumbnailPath } from '@vue-storefront/core/helpers'

export const ARTIST_CATEGORY_LEVEL = 5
export const NO_SELECTION = 'no_selection'
export const URL_SCHEME_SEPARATOR = '://'

/**
 * Prefix a relative path with the storefront origin so email clients can load it
 *
 * @param {string} url
 * @param {string} origin
 * @returns {string}
 */
export const toAbsoluteUrl = (url, origin) => {
  if (!url) {
    return ''
  }

  return url.includes(URL_SCHEME_SEPARATOR) ? url : origin + url
}

/**
 * Find the artist category the product belongs to
 *
 * @param {Object} product
 * @param {Array} menuCategories
 * @returns {Object|undefined}
 */
export const findArtistCategory = (product, menuCategories) => {
  if (!Array.isArray(menuCategories)) {
    return undefined
  }

  const categoryIds = Array.isArray(product?.category)
    ? product.category.map(category => Number(category.category_id))
    : []

  return menuCategories.find(category =>
    category.level === ARTIST_CATEGORY_LEVEL && category.is_artist && categoryIds.includes(category.id)
  )
}

/**
 * Get the absolute URL of the artist photo, or an empty string when the artist has none
 *
 * @param {Object} product
 * @param {Array} menuCategories
 * @param {string} origin
 * @returns {string}
 */
export const getArtistImageUrl = (product, menuCategories, origin) => {
  const artist = findArtistCategory(product, menuCategories)

  return toAbsoluteUrl(artist?.image, origin)
}

/**
 * Get the absolute URL of the product's own image, or an empty string when it has none
 *
 * @param {Object} product
 * @param {string} origin
 * @returns {string}
 */
export const getProductImageUrl = (product, origin) => {
  const thumbnail = productThumbnailPath(product)

  if (!thumbnail || thumbnail === NO_SELECTION) {
    return ''
  }

  if (thumbnail.includes(URL_SCHEME_SEPARATOR)) {
    return thumbnail
  }

  const path = getThumbnailPath(thumbnail, config.products.thumbnails.width, config.products.thumbnails.height)

  if (!path || path === config.images.productPlaceholder) {
    return ''
  }

  return toAbsoluteUrl(path, origin)
}

/**
 * Get the absolute URL of the image used when neither the product nor its artist has one
 *
 * @param {string} origin
 * @returns {string}
 */
export const getFallbackImageUrl = origin => {
  return toAbsoluteUrl(config.klaviyo?.fallbackImageUrl || config.images.productPlaceholder, origin)
}

/**
 * Resolve the image sent to Klaviyo: product image, then artist photo, then the fallback image
 *
 * @param {Object} product
 * @param {Array} menuCategories
 * @param {string} origin
 * @returns {string}
 */
export const resolveImageUrl = (product, menuCategories, origin) => {
  return getProductImageUrl(product, origin) ||
    getArtistImageUrl(product, menuCategories, origin) ||
    getFallbackImageUrl(origin)
}
